<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="breadcrumbItems" />
    
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Header -->
            <div class="row mb-4">
              <div class="col-sm-8">
                <div class="d-flex align-items-center">
                  <div class="flex-shrink-0 me-3">
                    <div class="avatar-sm rounded-circle bg-soft-primary">
                      <div class="avatar-title rounded-circle bg-primary">
                        <i class="ri-clipboard-line font-size-18 text-white"></i>
                      </div>
                    </div>
                  </div>
                  <div class="flex-grow-1">
                    <h4 class="card-title mb-1">{{ pageTitle }}</h4>
                    <p class="card-title-desc text-muted mb-0">
                      {{ isEditing ? 'Edite as informações do tipo de exame' : 'Preencha os dados para criar um novo tipo de exame' }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="col-sm-4">
                <div class="text-sm-end">
                  <router-link 
                    to="/configuration/exam-types" 
                    class="btn btn-secondary btn-rounded waves-effect waves-light"
                  >
                    <i class="ri-arrow-left-line align-middle me-2"></i>
                    Voltar à Lista
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Formulário -->
            <form @submit.prevent="saveExamType">
              <div class="row">
                <div class="col-lg-8">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <h5 class="card-title mb-4">
                        <i class="ri-file-text-line me-2 text-primary"></i>
                        Informações Básicas
                      </h5>

                      <div class="row">
                        <div class="col-md-6">
                          <div class="mb-3">
                            <label for="code" class="form-label fw-medium">
                              Código <span class="text-danger">*</span>
                            </label>
                            <input
                              id="code"
                              v-model="form.code"
                              type="text"
                              class="form-control"
                              :class="{ 'is-invalid': errors.code }"
                              placeholder="Ex: RX001"
                              maxlength="20"
                              required
                            >
                            <div v-if="errors.code" class="invalid-feedback">
                              {{ errors.code }}
                            </div>
                          </div>
                        </div>

                        <div class="col-md-6">
                          <div class="mb-3">
                            <label for="category" class="form-label fw-medium">
                              Categoria
                            </label>
                            <b-form-select
                              id="category"
                              v-model="form.category"
                              :options="categoryOptions"
                              class="form-select"
                              :class="{ 'is-invalid': errors.category }"
                            >
                              <template #first>
                                <option value="">Selecione uma categoria</option>
                              </template>
                            </b-form-select>
                            <div v-if="errors.category" class="invalid-feedback">
                              {{ errors.category }}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div class="mb-3">
                        <label for="name" class="form-label fw-medium">
                          Nome <span class="text-danger">*</span>
                        </label>
                        <input
                          id="name"
                          v-model="form.name"
                          type="text"
                          class="form-control"
                          :class="{ 'is-invalid': errors.name }"
                          placeholder="Ex: Radiografia de Tórax"
                          maxlength="100"
                          required
                        >
                        <div v-if="errors.name" class="invalid-feedback">
                          {{ errors.name }}
                        </div>
                      </div>

                      <div class="mb-3">
                        <label for="description" class="form-label fw-medium">
                          Descrição
                        </label>
                        <textarea
                          id="description"
                          v-model="form.description"
                          class="form-control"
                          :class="{ 'is-invalid': errors.description }"
                          rows="4"
                          placeholder="Descreva o tipo de exame..."
                          maxlength="500"
                        ></textarea>
                        <div v-if="errors.description" class="invalid-feedback">
                          {{ errors.description }}
                        </div>
                        <small class="text-muted">
                          {{ form.description ? form.description.length : 0 }}/500 caracteres
                        </small>
                      </div>

                      <div class="mb-3">
                        <div class="form-check form-switch">
                          <input
                            id="active"
                            v-model="form.active"
                            class="form-check-input"
                            type="checkbox"
                          >
                          <label class="form-check-label fw-medium" for="active">
                            Tipo de exame ativo
                          </label>
                          <small class="text-muted d-block">
                            Apenas tipos ativos podem ser utilizados em novos exames
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-lg-4">
                  <div class="card border-0 shadow-sm">
                    <div class="card-body">
                      <h5 class="card-title mb-4">
                        <i class="ri-information-line me-2 text-info"></i>
                        Resumo
                      </h5>

                      <div class="mb-3">
                        <small class="text-muted fw-medium d-block mb-1">Status:</small>
                        <span 
                          v-if="form.active" 
                          class="badge badge-soft-success"
                        >
                          <i class="ri-check-line me-1"></i>Ativo
                        </span>
                        <span v-else class="badge badge-soft-warning">
                          <i class="ri-pause-line me-1"></i>Inativo
                        </span>
                      </div>

                      <div class="mb-3" v-if="form.code">
                        <small class="text-muted fw-medium d-block mb-1">Código:</small>
                        <span class="badge badge-soft-primary">{{ form.code }}</span>
                      </div>

                      <div class="mb-3" v-if="form.category">
                        <small class="text-muted fw-medium d-block mb-1">Categoria:</small>
                        <span class="badge badge-soft-info">{{ form.category }}</span>
                      </div>

                      <div class="mt-4">
                        <button
                          type="submit"
                          class="btn btn-primary w-100 btn-rounded"
                          :disabled="loading"
                        >
                          <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status"></span>
                          <i v-else class="ri-save-line me-2"></i>
                          {{ isEditing ? 'Atualizar' : 'Salvar' }}
                        </button>

                        <button
                          type="button"
                          class="btn btn-outline-secondary w-100 mt-2"
                          @click="resetForm"
                          :disabled="loading"
                        >
                          <i class="ri-refresh-line me-2"></i>
                          Limpar Formulário
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import { examTypeService } from '@/api/examTypes.js'
import { authMixin } from '@/mixins/authMixin'

export default {
  name: 'ExamTypeForm',
  page: {
    title: "Formulário de Tipo de Exame",
    meta: [{ name: "description", content: "Criar ou editar tipo de exame" }],
  },
  mixins: [authMixin],
  components: {
    Layout,
    PageHeader
  },
  data() {
    return {
      loading: false,
      categories: [],
      form: {
        code: '',
        name: '',
        description: '',
        category: '',
        active: true
      },
      errors: {}
    }
  },
  computed: {
    isEditing() {
      return this.$route.params.id !== undefined
    },
    pageTitle() {
      return this.isEditing ? 'Editar Tipo de Exame' : 'Novo Tipo de Exame'
    },
    breadcrumbItems() {
      return [
        {
          text: "Configurações",
          href: "/"
        },
        {
          text: "Tipos de Exames",
          href: "/configuration/exam-types"
        },
        {
          text: this.isEditing ? "Editar" : "Novo",
          active: true
        }
      ]
    },
    categoryOptions() {
      return this.categories.map(cat => ({
        value: cat,
        text: cat
      }))
    }
  },
  async mounted() {
    await this.loadCategories()
    if (this.isEditing) {
      await this.loadExamType()
    }
  },
  methods: {
    async loadCategories() {
      try {
        const response = await examTypeService.getCategories()
        this.categories = response.data || []
      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
        this.$showError('Erro ao carregar categorias')
      }
    },

    async loadExamType() {
      const id = this.$route.params.id
      this.loading = true

      try {
        const response = await examTypeService.getExamTypeById(id)
        const examType = response.data
        
        this.form = {
          code: examType.code || '',
          name: examType.name || '',
          description: examType.description || '',
          category: examType.category || '',
          active: examType.active !== false
        }
      } catch (error) {
        console.error('Erro ao carregar tipo de exame:', error)
        this.$showError('Erro ao carregar tipo de exame')
        this.$router.push('/configuration/exam-types')
      } finally {
        this.loading = false
      }
    },

    validateForm() {
      this.errors = {}

      if (!this.form.code?.trim()) {
        this.errors.code = 'Código é obrigatório'
      } else if (this.form.code.length < 2) {
        this.errors.code = 'Código deve ter pelo menos 2 caracteres'
      }

      if (!this.form.name?.trim()) {
        this.errors.name = 'Nome é obrigatório'
      } else if (this.form.name.length < 3) {
        this.errors.name = 'Nome deve ter pelo menos 3 caracteres'
      }

      if (this.form.description && this.form.description.length > 500) {
        this.errors.description = 'Descrição não pode ter mais que 500 caracteres'
      }

      return Object.keys(this.errors).length === 0
    },

    async saveExamType() {
      if (!this.validateForm()) {
        this.$showError('Por favor, corrija os erros do formulário')
        return
      }

      this.loading = true

      try {
        const payload = {
          code: this.form.code.trim(),
          name: this.form.name.trim(),
          description: this.form.description?.trim() || null,
          category: this.form.category || null,
          active: this.form.active
        }

        if (this.isEditing) {
          await examTypeService.updateExamType(this.$route.params.id, payload)
          this.$showSuccess('Tipo de exame atualizado com sucesso')
        } else {
          await examTypeService.createExamType(payload)
          this.$showSuccess('Tipo de exame criado com sucesso')
        }

        this.$router.push('/configuration/exam-types')
      } catch (error) {
        console.error('Erro ao salvar tipo de exame:', error)
        
        if (error.response?.status === 400) {
          const errorData = error.response.data
          if (errorData.detail?.includes('código')) {
            this.errors.code = 'Este código já está em uso'
          } else if (errorData.detail?.includes('nome')) {
            this.errors.name = 'Este nome já está em uso'
          } else {
            this.$showError(errorData.detail || 'Dados inválidos')
          }
        } else {
          this.$showError('Erro ao salvar tipo de exame')
        }
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.form = {
        code: '',
        name: '',
        description: '',
        category: '',
        active: true
      }
      this.errors = {}
    }
  }
}
</script>

<style scoped>
.card {
  border-radius: 10px;
  border: 1px solid rgba(0,0,0,0.05);
}

.shadow-sm {
  box-shadow: 0 2px 4px rgba(0,0,0,0.08) !important;
}

.form-control:focus, .form-select:focus {
  border-color: #556ee6;
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

.btn-rounded {
  border-radius: 30px;
}

.btn:focus, .btn.focus {
  box-shadow: none;
}

.badge-soft-primary {
  color: #556ee6;
  background-color: rgba(85, 110, 230, 0.18);
  border: 1px solid rgba(85, 110, 230, 0.1);
}

.badge-soft-success {
  color: #34c38f;
  background-color: rgba(52, 195, 143, 0.18);
  border: 1px solid rgba(52, 195, 143, 0.1);
}

.badge-soft-warning {
  color: #f1b44c;
  background-color: rgba(241, 180, 76, 0.18);
  border: 1px solid rgba(241, 180, 76, 0.1);
}

.badge-soft-info {
  color: #50a5f1;
  background-color: rgba(80, 165, 241, 0.18);
  border: 1px solid rgba(80, 165, 241, 0.1);
}

.form-check-input:checked {
  background-color: #556ee6;
  border-color: #556ee6;
}

.form-check-input:focus {
  box-shadow: 0 0 0 0.2rem rgba(85, 110, 230, 0.25);
}

.is-invalid {
  border-color: #f46a6a;
}

.invalid-feedback {
  display: block;
}

@media (max-width: 768px) {
  .text-sm-end {
    text-align: left !important;
    margin-top: 1rem;
  }
  
  .btn-rounded {
    width: 100%;
  }
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.card {
  animation: fadeIn 0.3s ease-out;
}

.font-size-18 { font-size: 18px !important; }
.fw-medium { font-weight: 500 !important; }
</style>
